/**
 * v136.0 — useCloudSync hook.
 *
 * Bridges the IdentityProvider state to the local Range Log store via the
 * cloudSync client.
 *
 * Behaviors:
 *
 *   1. On sign-in transition (anonymous → authed), if the local store has
 *      sessions not yet in cloud, fire a one-time "Sync your existing N
 *      local sessions?" prompt. On accept, push them all and remember the
 *      decision so we don't re-prompt.
 *
 *   2. On sign-in or page load while authed, pull cloud sessions and merge
 *      into the local store. Anything cloud-only that we don't have locally
 *      gets restored (e.g., user on a new device sees their previous sessions).
 *
 *   3. On every local session save / delete while authed, mirror to cloud.
 *      Best-effort — errors surface as a toast but don't block local persistence.
 *
 * Anonymous mode: this hook is a no-op. Local storage is the only path.
 */

import { useCallback, useEffect, useState } from 'react';
import { useIdentity } from '@/auth/useIdentity';
import {
  pushSession,
  pullSessions,
  deleteCloudSession,
  mergeSessions,
  verifyIdentity,
} from './cloudSync';
import type { ShotSession } from '@/range/types';

const BACKFILL_DECIDED_KEY = 'sparrow.cloudSync.backfillDecided';
const LAST_SYNC_KEY = 'sparrow.cloudSync.lastSyncAt';

export type CloudSyncState = 'anonymous' | 'idle' | 'syncing' | 'error';

export interface UseCloudSync {
  state: CloudSyncState;
  /** True if the user is signed in but has local sessions not yet in cloud. */
  backfillPending: boolean;
  /** Number of local sessions eligible for backfill. */
  backfillCount: number;
  /** Run the one-time backfill (push every local session). */
  runBackfill: () => Promise<void>;
  /** Decline the backfill — local sessions stay local, future ones sync. */
  declineBackfill: () => void;
  /** Manually pull cloud + merge into local. */
  refresh: () => Promise<void>;
  /** Mirror a single session to cloud. Best-effort. */
  mirror: (session: ShotSession) => Promise<void>;
  /** Mirror a delete to cloud. Best-effort. */
  mirrorDelete: (sessionId: string) => Promise<void>;
  /** Last successful sync timestamp (ms). */
  lastSyncAt: number | null;
  /** Latest error message, if any. */
  errorMessage: string | null;
}

interface UseCloudSyncOpts {
  /** Read local sessions for backfill detection. */
  getLocalSessions: () => readonly ShotSession[];
  /** Replace the local session store with merged sessions. */
  replaceLocalSessions: (next: ShotSession[]) => void;
}

export function useCloudSync(opts: UseCloudSyncOpts): UseCloudSync {
  const { identity, loading } = useIdentity();
  const [state, setState] = useState<CloudSyncState>('anonymous');
  const [backfillPending, setBackfillPending] = useState(false);
  const [backfillCount, setBackfillCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lastSyncAt, setLastSyncAt] = useState<number | null>(() => {
    try {
      const raw = window.localStorage.getItem(LAST_SYNC_KEY);
      return raw ? Number(raw) : null;
    } catch {
      return null;
    }
  });

  const isAuthed = !loading && identity.signedIn && identity.customerHash;

  const persistLastSync = useCallback((ms: number) => {
    try {
      window.localStorage.setItem(LAST_SYNC_KEY, String(ms));
    } catch {}
    setLastSyncAt(ms);
  }, []);

  // On auth transition: verify, pull, merge, evaluate backfill.
  useEffect(() => {
    if (!isAuthed) {
      setState('anonymous');
      setBackfillPending(false);
      setBackfillCount(0);
      return;
    }
    let cancelled = false;
    void (async () => {
      setState('syncing');
      setErrorMessage(null);
      const verify = await verifyIdentity();
      if (cancelled) return;
      if (!verify.ok) {
        setState('error');
        setErrorMessage(`Identity check failed: ${verify.error ?? 'unknown'}`);
        return;
      }
      const pull = await pullSessions();
      if (cancelled) return;
      if (!pull.ok || !pull.data) {
        setState('error');
        setErrorMessage(`Pull failed: ${pull.error ?? 'unknown'}`);
        return;
      }
      const local = opts.getLocalSessions();
      const merged = mergeSessions(local, pull.data);
      opts.replaceLocalSessions(merged);

      // Backfill: sessions in local that are NOT in cloud (by id).
      const cloudIds = new Set(pull.data.map((s) => s.id));
      const localOnly = local.filter((s) => !cloudIds.has(s.id));
      const decided = window.localStorage.getItem(BACKFILL_DECIDED_KEY) === '1';
      if (localOnly.length > 0 && !decided) {
        setBackfillPending(true);
        setBackfillCount(localOnly.length);
      } else {
        setBackfillPending(false);
      }

      persistLastSync(Date.now());
      setState('idle');
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthed, opts, persistLastSync]);

  const runBackfill = useCallback(async () => {
    if (!isAuthed) return;
    setState('syncing');
    const local = opts.getLocalSessions();
    let failures = 0;
    for (const session of local) {
      const res = await pushSession(session);
      if (!res.ok) failures += 1;
    }
    try {
      window.localStorage.setItem(BACKFILL_DECIDED_KEY, '1');
    } catch {}
    setBackfillPending(false);
    persistLastSync(Date.now());
    if (failures > 0) {
      setState('error');
      setErrorMessage(`${failures} session(s) failed to sync — they'll retry on next save.`);
    } else {
      setState('idle');
      setErrorMessage(null);
    }
  }, [isAuthed, opts, persistLastSync]);

  const declineBackfill = useCallback(() => {
    try {
      window.localStorage.setItem(BACKFILL_DECIDED_KEY, '1');
    } catch {}
    setBackfillPending(false);
  }, []);

  const refresh = useCallback(async () => {
    if (!isAuthed) return;
    setState('syncing');
    const pull = await pullSessions();
    if (!pull.ok || !pull.data) {
      setState('error');
      setErrorMessage(`Pull failed: ${pull.error ?? 'unknown'}`);
      return;
    }
    const local = opts.getLocalSessions();
    opts.replaceLocalSessions(mergeSessions(local, pull.data));
    persistLastSync(Date.now());
    setState('idle');
    setErrorMessage(null);
  }, [isAuthed, opts, persistLastSync]);

  const mirror = useCallback(
    async (session: ShotSession) => {
      if (!isAuthed) return;
      const res = await pushSession(session);
      if (!res.ok) {
        setErrorMessage(`Sync failed for one session — will retry.`);
      } else {
        persistLastSync(Date.now());
        if (errorMessage) setErrorMessage(null);
      }
    },
    [isAuthed, persistLastSync, errorMessage],
  );

  const mirrorDelete = useCallback(
    async (sessionId: string) => {
      if (!isAuthed) return;
      const res = await deleteCloudSession(sessionId);
      if (!res.ok) {
        setErrorMessage(`Cloud delete failed for one session.`);
      }
    },
    [isAuthed],
  );

  return {
    state,
    backfillPending,
    backfillCount,
    runBackfill,
    declineBackfill,
    refresh,
    mirror,
    mirrorDelete,
    lastSyncAt,
    errorMessage,
  };
}
